import React, { useContext, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendEmailVerification, signOut
} from 'firebase/auth';
import { Button, Form, Message, Grid, Segment, Header, Container, Checkbox, Modal, Label } from 'semantic-ui-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import "./SignUp.css";
import { analytics, auth, logEvent } from '../firebaseConfig';
import { updateUserTokenCount, checkUserRegistration } from '../Utils/Utils';
import { useTranslation } from 'react-i18next';
import { EnvironmentContext } from '../Context/EnvironmentContext';
import Login from './Login';
import useCouponModal from '../Modals/useCouponModal';
import { usePostHog } from '@posthog/react';

function SignUp({ onSuccess, onCancel, contextMessage, showGuestButton = false }) {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const env = useContext(EnvironmentContext);
  const { markShowCouponModalForUser } = useCouponModal();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [termsErrorMessage, setTermsErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [loginOrSignUp, setLoginOrSignUp] = useState('signup');

  // Get the location where the user was before
  const from = location.state?.from || "/";

  const provider = new GoogleAuthProvider();

  const handleSignUp = async (e) => {
    e.preventDefault();
  if (analytics) logEvent(analytics, 'user_try_sign_up');
    setError('');
    setEmailError(false);
    setEmailErrorMessage('');
    setPasswordError(false);
    setPasswordErrorMessage('');
    setTermsError(false);
    setTermsErrorMessage('');
    setLoading(true);

    if (!validateEmail(email)) {
      setEmailError(true);
      setEmailErrorMessage(t('InvalidEmail'));
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage(t('PasswordTooShort'));
      setLoading(false);
      return;
    }

    if (!termsAccepted) {
      setTermsError(true);
      setTermsErrorMessage(t('AcceptTermsRequired'));
      setLoading(false);
      return;
    }

    try {
      var tokenCountBeforeSignUp = env.userTokenCount;
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      // Send verification email
      await sendEmailVerification(user);

      // Update token count but don't navigate away
      await handleTokenInitialisationWhenCreateAccount(user);

      // Show message to check email
      setError('');
      setSuccess(t('VerificationEmailSent'));

      // Sign out until email is verified
      await signOut(auth);
      env.setUserTokenCount(tokenCountBeforeSignUp);

      // If you want to keep them signed in but restrict access:
      // env.user = user;

      // Mark to show coupon modal after verification
      markShowCouponModalForUser(user.uid);

      posthog?.identify(user.uid, { email: user.email });
      posthog?.capture('user_signed_up', { method: 'email' });

      if (onSuccess) {
  if (analytics) logEvent(analytics, 'user_managed_signup');
        onSuccess();
      } else {
        // Just show the success message and keep the user on the current page
        // The success message is already set above with setSuccess(t('VerificationEmailSent'))
      }
    } catch (error) {
      setError(getUserFriendlyError(error.code));
    } finally {
      setLoading(false);
    }
  };

  const handleTokenInitialisationWhenCreateAccount = async (user) => {
    const isRegistered = await checkUserRegistration(user.uid);
    if (!isRegistered) {
      const updateSuccess = await updateUserTokenCount(user.uid, 100);
      if (!updateSuccess) {
        throw new Error('Failed to update user token count.');
      }
    }
  };

  const handleGoogleSignUp = async () => {
  if (analytics) logEvent(analytics, 'user_try_google_signup');
    if (!termsAccepted) {
      setTermsError(true);
      setTermsErrorMessage(t('AcceptTermsRequired'));
      return;
    }

    setGoogleLoading(true);
    try {
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;
      env.user = user;
      await handleTokenInitialisationWhenCreateAccount(user);

      // Mark to show coupon modal after verification
      markShowCouponModalForUser(user.uid);

      posthog?.identify(user.uid, { email: user.email });
      posthog?.capture('user_google_signed_up');

      if (onSuccess) {
  if (analytics) logEvent(analytics, 'user_managed_google_signup');
        onSuccess();
      } else {
        navigate(from);
      }
    } catch (error) {
      setError(getUserFriendlyError(error.code));
    } finally {
      setGoogleLoading(false);
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    if (strength < 3) return 'weak';
    if (strength < 4) return 'medium';
    return 'strong';
  };

  const getUserFriendlyError = (errorCode) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return t('EmailAlreadyInUse');
      case 'auth/invalid-email':
        return t('InvalidEmail');
      case 'auth/weak-password':
        return t('WeakPassword');
      case 'auth/user-disabled':
        return t('UserDisabled');
      case 'auth/too-many-requests':
        return t('TooManyRequests');
      default:
        return t('SignUpFailed');
    }
  };

  return (
    loginOrSignUp === 'login' ? (
      <Login onSuccess={onSuccess} onCancel={onCancel} contextMessage={contextMessage} showGuestButton={showGuestButton} />
    ) : (
      <Modal open={true} onClose={onCancel} size='small' closeIcon>
        <Modal.Header style={{ backgroundColor: '#14b8a6', color: 'white' }}>{t('SignUp')}</Modal.Header>
        <Modal.Content>
          {contextMessage && (
            <Message style={{ backgroundColor: '#f3f4f6', borderColor: '#14b8a6', color: '#14b8a6' }}>
              <p>{contextMessage}</p>
            </Message>
          )}
          <Form onSubmit={handleSignUp} size="large">
            <Form.Input
              error={emailError}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fluid
              icon="mail"
              iconPosition="left"
              placeholder="Email"
              type="email"
            />
            {emailError && <Label pointing color='red'>{emailErrorMessage}</Label>}
            <Form.Input
              error={passwordError}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordStrength(getPasswordStrength(e.target.value));
              }}
              fluid
              icon="lock"
              iconPosition="left"
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              action={{
                icon: showPassword ? "eye slash" : "eye",
                onClick: () => setShowPassword(!showPassword),
                type: "button"
              }}
            />
            {passwordError && <Label pointing color='red'>{passwordErrorMessage}</Label>}
            {password && (
              <div style={{ textAlign: 'left', marginTop: '5px' }}>
                <span style={{ color: passwordStrength === 'weak' ? 'red' : passwordStrength === 'medium' ? 'orange' : 'green' }}>
                  {t('PasswordStrength')}: {t(passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1))}
                </span>
              </div>
            )}
            <Form.Field
              style={{ textAlign: 'left', marginBottom: '1rem' }}
              error={termsError}
            >
              <Checkbox
                label={
                  <label>
                    {t('AcceptTerms')}{' '}
                    <Link to="/CGU" target="_blank">
                      {t('TermsOfService')}
                    </Link>
                  </label>
                }
                checked={termsAccepted}
                onChange={(_, data) => setTermsAccepted(data.checked)}
              />
              {termsError && <Label pointing color='red'>{termsErrorMessage}</Label>}
            </Form.Field>
            {error && <Message error visible header="Sign Up Failed" content={error} />}
            {success && <Message success visible content={success} />}
            <Button type="submit" color="teal" fluid size="large" loading={loading} disabled={loading}>
              {t('SignUp')}
            </Button>
          </Form>
          <Button
            color="google plus"
            fluid
            size="large"
            onClick={handleGoogleSignUp}
            style={{ marginTop: '1rem' }}
            loading={googleLoading}
            disabled={googleLoading}
          >
            {t('SignUpGoogle')}
          </Button>
          <Message>
            {t('AlreadyAccount')}{' '}
            <Button onClick={() => setLoginOrSignUp('login')} color="teal">
              {t('LogIn')}
            </Button>
          </Message>
          {showGuestButton && (
            <Button
              basic
              fluid
              size="large"
              onClick={onCancel}
              style={{ marginTop: '1rem' }}
            >
              {t('GuestContinue')}
            </Button>
          )}
        </Modal.Content>
      </Modal>
    )
  );
}

export default SignUp;